import { useState, useEffect } from "react";
import { Trophy } from "lucide-react";
import { Button } from "../ui/button";

const CHOICES = [
    { id: "rock",     emoji: "✊", label: "Rock" },
    { id: "paper",    emoji: "✋", label: "Paper" },
    { id: "scissors", emoji: "✌️", label: "Scissors" }
];

export function GameRPS({ match, currentUserId, onMove, onLeave }) {
    const state = match.state;
    const me = match.players.find((p) => p.userId === String(currentUserId));
    const opp = match.players.find((p) => p.userId !== String(currentUserId));
    const myWins = state.wins[me.userId] || 0;
    const oppWins = state.wins[opp.userId] || 0;
    const lastRound = state.history?.[state.history.length - 1];

    const [myPick, setMyPick] = useState(null);

    // Reset local pick when round advances
    useEffect(() => {
        setMyPick(null);
    }, [state.round]);

    const handlePick = (choice) => {
        if (state.winner) return;
        if (myPick) return;
        setMyPick(choice);
        onMove({ choice });
    };

    const waitingForOpp = myPick && !state.winner;

    return (
        <div className="flex flex-col items-center">
            {/* Score header */}
            <div className="flex items-center justify-between w-full max-w-md gap-3 mb-3">
                <ScoreSide name={me.userName + " (you)"} wins={myWins} />
                <div className="text-center">
                    <div className="text-xs text-gray-400 font-semibold">ROUND</div>
                    <div className="text-xl font-bold text-gray-900">{state.round}/{state.totalRounds}</div>
                </div>
                <ScoreSide name={opp.userName} wins={oppWins} right />
            </div>

            {/* Last round result */}
            {lastRound && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 mb-3 text-xs text-gray-700 text-center w-full max-w-md">
                    <span className="opacity-60">Last:</span>{" "}
                    <span className="font-medium">You {iconFor(lastRound.choices[me.userId])}</span>
                    <span className="opacity-50 mx-2">vs</span>
                    <span className="font-medium">{opp.userName} {iconFor(lastRound.choices[opp.userId])}</span>
                    <span className="ml-2 font-bold">
                        {lastRound.winnerUserId === "draw" ? "Draw" :
                         lastRound.winnerUserId === me.userId ? "✅ Won" : "❌ Lost"}
                    </span>
                </div>
            )}

            {/* Choice buttons */}
            {!state.winner && (
                <div className="grid grid-cols-3 gap-3 w-full max-w-md">
                    {CHOICES.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => handlePick(c.id)}
                            disabled={!!myPick}
                            className={`aspect-square rounded-xl border-2 flex flex-col items-center justify-center transition-all ${
                                myPick === c.id ? "border-pink-500 bg-pink-50 scale-95" :
                                myPick ? "border-gray-200 bg-gray-50 opacity-40" :
                                "border-gray-200 bg-white hover:border-pink-400 hover:bg-pink-50 hover:scale-105 cursor-pointer"
                            }`}
                        >
                            <span className="text-4xl mb-1">{c.emoji}</span>
                            <span className="text-xs font-medium text-gray-700">{c.label}</span>
                        </button>
                    ))}
                </div>
            )}

            {waitingForOpp && (
                <p className="text-xs text-gray-500 mt-3 animate-pulse">Waiting for {opp.userName} to choose...</p>
            )}

            {state.winner && (
                <EndCard winner={state.winner} currentUserId={currentUserId} onLeave={onLeave} />
            )}
        </div>
    );
}

function ScoreSide({ name, wins, right }) {
    return (
        <div className={`flex-1 ${right ? "text-right" : ""}`}>
            <div className="text-xs text-gray-600 truncate">{name}</div>
            <div className="text-2xl font-bold text-gray-900 tabular-nums">{wins}</div>
        </div>
    );
}

function iconFor(choice) {
    const c = CHOICES.find((x) => x.id === choice);
    return c ? c.emoji : "?";
}

function EndCard({ winner, currentUserId, onLeave }) {
    const youWon = winner === String(currentUserId);
    const draw = winner === "draw";
    return (
        <div className={`mt-4 px-4 py-3 rounded-lg border text-center max-w-md ${
            draw ? "bg-gray-50 border-gray-200" :
            youWon ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
        }`}>
            <Trophy className={`h-6 w-6 mx-auto mb-1 ${draw ? "text-gray-400" : youWon ? "text-emerald-600" : "text-red-500"}`} />
            <div className={`text-sm font-bold ${draw ? "text-gray-700" : youWon ? "text-emerald-800" : "text-red-700"}`}>
                {draw ? "It's a draw!" : youWon ? "You won the match!" : "You lost"}
            </div>
            <Button size="sm" variant="outline" onClick={onLeave} className="mt-2">Back to lobby</Button>
        </div>
    );
}
